import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MapPin, Mail, Clock, Send, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAP_SRC =
  "https://www.google.com/maps?q=38885+Stayton+Scio+Rd,+Scio,+OR+97374&output=embed";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    relationship: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please share your name, email, and a short message.");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/inquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        relationship: form.relationship.trim() || null,
        message: form.message.trim(),
      });
      toast.success("Thank you — we received your note. Karie will reach out soon.");
      setForm({ name: "", email: "", phone: "", relationship: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "We couldn't send your message. Please call (503) 394-2321."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-[#F9F8F6]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left: Form */}
          <div className="lg:col-span-7">
            <p className="reveal text-xs uppercase tracking-[0.3em] font-semibold text-[#667B68]">
              Contact
            </p>
            <h2
              className="reveal font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#1C2A24] mt-4"
              data-testid="contact-title"
              style={{ transitionDelay: "80ms" }}
            >
              Let&rsquo;s talk about
              <br />
              your loved one.
            </h2>
            <p
              className="reveal mt-6 text-[17px] leading-relaxed text-[#3E574B] max-w-xl"
              style={{ transitionDelay: "140ms" }}
            >
              Whether you&rsquo;re planning ahead or in the middle of a hard
              transition — we&rsquo;re here. Send a note, or call Karie
              directly.
            </p>

            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="reveal mt-10 bg-white border border-[#E5E1D8] rounded-3xl p-6 sm:p-8 shadow-[0_12px_40px_-24px_rgba(44,76,59,0.2)]"
              style={{ transitionDelay: "200ms" }}
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Your name" required>
                  <input
                    data-testid="contact-input-name"
                    required
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    className="w-full bg-white border border-[#E5E1D8] focus:border-[#667B68] focus:ring-2 focus:ring-[#667B68]/30 focus:outline-none rounded-xl px-4 py-3 text-[15px] placeholder:text-[#3E574B]/50 transition"
                    placeholder="Jane Doe"
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    data-testid="contact-input-email"
                    required
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    className="w-full bg-white border border-[#E5E1D8] focus:border-[#667B68] focus:ring-2 focus:ring-[#667B68]/30 focus:outline-none rounded-xl px-4 py-3 text-[15px] placeholder:text-[#3E574B]/50 transition"
                    placeholder="you@example.com"
                  />
                </Field>
                <Field label="Phone">
                  <input
                    data-testid="contact-input-phone"
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    className="w-full bg-white border border-[#E5E1D8] focus:border-[#667B68] focus:ring-2 focus:ring-[#667B68]/30 focus:outline-none rounded-xl px-4 py-3 text-[15px] placeholder:text-[#3E574B]/50 transition"
                    placeholder="(___) ___-____"
                  />
                </Field>
                <Field label="Relationship">
                  <input
                    data-testid="contact-input-relationship"
                    type="text"
                    value={form.relationship}
                    onChange={update("relationship")}
                    className="w-full bg-white border border-[#E5E1D8] focus:border-[#667B68] focus:ring-2 focus:ring-[#667B68]/30 focus:outline-none rounded-xl px-4 py-3 text-[15px] placeholder:text-[#3E574B]/50 transition"
                    placeholder="Daughter, son, spouse…"
                  />
                </Field>
              </div>
              <div className="mt-4">
                <Field label="How can we help?" required>
                  <textarea
                    data-testid="contact-input-message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    className="w-full bg-white border border-[#E5E1D8] focus:border-[#667B68] focus:ring-2 focus:ring-[#667B68]/30 focus:outline-none rounded-xl px-4 py-3 text-[15px] placeholder:text-[#3E574B]/50 transition resize-none"
                    placeholder="Tell us a little about your loved one, their care needs, and your timeline."
                  />
                </Field>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-btn"
                className="mt-6 inline-flex items-center justify-center gap-2 bg-[#2C4C3B] text-white hover:bg-[#1f372a] disabled:opacity-60 disabled:cursor-not-allowed transition-colors rounded-full px-8 py-4 font-medium w-full sm:w-auto"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Message
                  </>
                )}
              </button>

              <p className="text-xs text-[#667B68] mt-4 leading-relaxed">
                We read every message personally. For urgent placement, please
                call us directly.
              </p>
            </form>
          </div>

          {/* Right: Info + Map */}
          <div className="lg:col-span-5">
            <div className="reveal bg-[#2C4C3B] text-white rounded-3xl p-8 sm:p-10" style={{ transitionDelay: "100ms" }}>
              <p className="text-xs uppercase tracking-[0.3em] font-semibold text-[#F4F1EA]/70">
                Reach Karie
              </p>
              <h3 className="font-heading text-3xl mt-3">Two steps away.</h3>

              <div className="mt-8 space-y-5">
                <InfoRow
                  icon={Phone}
                  label="Call"
                  value="(503) 394-2321"
                  href="tel:+15033942321"
                  testId="contact-info-call"
                />
                <InfoRow
                  icon={Mail}
                  label="Email"
                  value="karie_richard@yahoo.com"
                  href="mailto:karie_richard@yahoo.com"
                  testId="contact-info-email"
                />
                <InfoRow
                  icon={MapPin}
                  label="Visit"
                  value={
                    <>
                      38885 Stayton Scio Rd
                      <br />
                      Scio, OR 97374
                    </>
                  }
                  href="https://www.google.com/maps?q=38885+Stayton+Scio+Rd,+Scio,+OR+97374"
                  external
                  testId="contact-info-address"
                />
                <InfoRow
                  icon={Clock}
                  label="Hours"
                  value={
                    <>
                      On-site care 24/7
                      <br />
                      Tours by appointment
                    </>
                  }
                  testId="contact-info-hours"
                />
              </div>
            </div>

            <div
              className="reveal mt-6 rounded-3xl overflow-hidden border border-[#E5E1D8] bg-white"
              data-testid="contact-map"
              style={{ transitionDelay: "180ms" }}
            >
              <iframe
                title="Map to Carlene's Comfort Care, 38885 Stayton Scio Rd, Scio, OR 97374"
                src={MAP_SRC}
                width="100%"
                height="320"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.2em] font-semibold text-[#3E574B] mb-2">
        {label}
        {required && <span className="text-[#C27A62] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}

function InfoRow({ icon: Icon, label, value, href, external, testId }) {
  const content = (
    <div className="flex items-start gap-4">
      <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm shrink-0">
        <Icon className="w-4 h-4 text-[#F4F1EA]" />
      </span>
      <div>
        <p className="text-[11px] uppercase tracking-[0.25em] text-[#F4F1EA]/70 font-semibold">
          {label}
        </p>
        <p className="font-heading text-xl leading-snug mt-1 text-white">
          {value}
        </p>
      </div>
    </div>
  );
  if (href) {
    return (
      <a
        href={href}
        data-testid={testId}
        target={external ? "_blank" : undefined}
        rel={external ? "noreferrer noopener" : undefined}
        className="block group hover:translate-x-1 transition-transform"
      >
        {content}
      </a>
    );
  }
  return (
    <div data-testid={testId}>{content}</div>
  );
}
